import { useCallback, useRef } from "react";

export type DataRoomListItem = {
  id: string;
  type: "file" | "folder";
};

/**
 * Checkbox / row selection with shift-click range support across the
 * combined folder+file list (matching on-screen order).
 */
export function useDataRoomListSelection(args: {
  items: DataRoomListItem[];
  selectedFiles: string[];
  selectedFolders: string[];
  setSelectedFiles: (ids: string[]) => void;
  setSelectedFolders: (ids: string[]) => void;
}) {
  const {
    items,
    selectedFiles,
    selectedFolders,
    setSelectedFiles,
    setSelectedFolders,
  } = args;

  const anchorIdRef = useRef<string | null>(null);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const applySelection = useCallback(
    (next: DataRoomListItem[]) => {
      setSelectedFolders(
        next.filter((item) => item.type === "folder").map((item) => item.id),
      );
      setSelectedFiles(
        next.filter((item) => item.type === "file").map((item) => item.id),
      );
    },
    [setSelectedFiles, setSelectedFolders],
  );

  const selectItem = useCallback(
    (id: string, type: "file" | "folder", opts?: { shiftKey?: boolean }) => {
      const list = itemsRef.current;
      const shiftKey = Boolean(opts?.shiftKey);

      if (shiftKey && anchorIdRef.current) {
        const anchorIndex = list.findIndex(
          (item) => item.id === anchorIdRef.current,
        );
        const currentIndex = list.findIndex((item) => item.id === id);

        if (anchorIndex !== -1 && currentIndex !== -1) {
          const start = Math.min(anchorIndex, currentIndex);
          const end = Math.max(anchorIndex, currentIndex);
          applySelection(list.slice(start, end + 1));
          return;
        }
      }

      const isSelected =
        type === "folder"
          ? selectedFolders.includes(id)
          : selectedFiles.includes(id);

      if (isSelected) {
        if (type === "folder") {
          setSelectedFolders(selectedFolders.filter((fid) => fid !== id));
        } else {
          setSelectedFiles(selectedFiles.filter((fid) => fid !== id));
        }
      } else {
        if (type === "folder") {
          setSelectedFolders([...selectedFolders, id]);
        } else {
          setSelectedFiles([...selectedFiles, id]);
        }
      }

      anchorIdRef.current = id;
    },
    [
      applySelection,
      selectedFiles,
      selectedFolders,
      setSelectedFiles,
      setSelectedFolders,
    ],
  );

  const selectItemsByIds = useCallback(
    (ids: string[]) => {
      const idSet = new Set(ids);
      const matched = itemsRef.current.filter((item) => idSet.has(item.id));
      applySelection(matched);
      if (matched.length > 0) {
        anchorIdRef.current = matched[matched.length - 1].id;
      }
    },
    [applySelection],
  );

  return {
    selectItem,
    selectItemsByIds,
  };
}
